import { ChangeEvent, useState, useEffect } from 'react';
import css from './index.module.scss';
import { Typography, Checkbox, IconNode } from '@components/base';
import {
  BoxContainer,
  SelectLabel,
  TextAreaLabel,
  TextField,
  UploadButton
} from '@components/common';
import { Controller, useFormContext } from 'react-hook-form';
import Images from '@assets/images';
import { IProductForm, ITrademarkName, ProductInfo } from '@helpers/types/product';
import { getProductTrademarkOptions } from '@services/product.service';
import notify from '@helpers/toastify-helper';

interface ProductDetailContainerStates {
  options: ITrademarkName[];
  isLoading: boolean;
  isOpen: boolean;
  docUploaded: boolean;
}

interface ProductDetailProps {
  editMode?: boolean;
}

const ProductDetailContainer = (props: ProductDetailProps) => {
  const [productStates, setProductStates] = useState<ProductDetailContainerStates>({
    options: [],
    isLoading: false,
    isOpen: false,
    docUploaded: false
  });
  const { options } = productStates;
  const { control, setValue, watch } = useFormContext<IProductForm>();
  const { editMode = false } = props;

  const fetchTrademarkOptions = async () => {
    setProductStates((prevState) => ({ ...prevState, isLoading: true }));
    const response = await getProductTrademarkOptions();
    if (response.success) {
      setProductStates((prevState) => ({
        ...prevState,
        isLoading: false,
        options: response.data
      }));
    } else {
      notify({
        severity: 'error',
        message: response?.error ?? 'Something Went Wrong, Contact Tech Team'
      });
    }
  };

  useEffect(() => {
    if (watch('msds_doc')) {
      setProductStates((prevState) => ({ ...prevState, docUploaded: true }));
    }
  }, [watch('msds_doc')]);

  useEffect(() => {
    fetchTrademarkOptions();
  }, []);

  return (
    <BoxContainer title="Details">
      <div className={css.boxWrapper}>
        <div className={css.rowWrapper}>
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                required
                autoFocus
                type="text"
                inputMode="text"
                enterKeyHint="next"
                label="Product Name"
                placeholder="Enter name"
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                endIcon={
                  fieldState.invalid && <IconNode src={Images.alertError} alt="error icon" />
                }
              />
            )}
          />
          <Controller
            name="CAS_number"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                required
                type="text"
                inputMode="numeric"
                enterKeyHint="next"
                label="CAS Number"
                placeholder="Enter CAS name"
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                endIcon={
                  fieldState.invalid && <IconNode src={Images.alertError} alt="error icon" />
                }
              />
            )}
          />
        </div>
        <div className={css.rowWrapper}>
          <Controller
            name="trademark_name"
            render={({ field, fieldState }) => (
              <SelectLabel
                {...field}
                label="Trademark Name"
                options={options}
                getOptionLabel={(option: any) => option.name}
                getOptionValue={(option: any) => option.product_trademark_prefix_id}
                rootClassName={css.fieldWrapper}
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                isClearable
              />
            )}
          />
        </div>
        <div className={css.rowWrapper}>
          <Controller
            name="description"
            control={control}
            render={({ field, fieldState }) => (
              <TextAreaLabel
                {...field}
                inputMode="text"
                label="Description"
                placeholder="Enter additional remarks/ requirements "
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                endIcon={
                  fieldState.invalid && <IconNode src={Images.alertError} alt="error icon" />
                }
              />
            )}
          />
        </div>
        <div className={css.rowWrapper}>
          <Controller
            name="packaging_details"
            control={control}
            render={({ field, fieldState }) => (
              <TextAreaLabel
                {...field}
                inputMode="text"
                label="Packaging Details"
                placeholder="Enter packaging details"
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                endIcon={
                  fieldState.invalid && <IconNode src={Images.alertError} alt="error icon" />
                }
              />
            )}
          />
        </div>
        <div className={css.rowWrapper}>
          <Controller
            name="remarks"
            control={control}
            render={({ field, fieldState }) => (
              <TextAreaLabel
                {...field}
                inputMode="text"
                label="Additional Remarks"
                placeholder="Enter additional remarks"
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                endIcon={
                  fieldState.invalid && <IconNode src={Images.alertError} alt="error icon" />
                }
              />
            )}
          />
        </div>
        {!editMode ? (
          <div>
            <Typography variant="label" className={css.label}>
              Upload MSDS
            </Typography>
            <div className={css.uploadContainer}>
              <div className={css.uploadContainerRow}>
                <div style={{ display: 'flex' }}>
                  {productStates.docUploaded ? (
                    <IconNode
                      className={css.uploadSuccess}
                      src={Images.illustrationGreen}
                      alt="success icon"
                    />
                  ) : (
                    ``
                  )}
                  <Typography>MSDS</Typography>
                </div>
                <Controller
                  name="msds_doc"
                  control={control}
                  render={({ field, fieldState }) => (
                    <div>
                      <UploadButton
                        {...field}
                        required
                        value={''}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          setValue(
                            'msds_doc',
                            e?.currentTarget?.files?.length ? e?.currentTarget?.files[0] : null
                          )
                        }
                      />
                    </div>
                  )}
                />
              </div>
            </div>
          </div>
        ) : (
          <></>
        )}
        <div className={css.rowWrapper}>
          <Controller
            name="hs_code"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                type="text"
                enterKeyHint="next"
                label="HS Code"
                placeholder="Enter HS Code"
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                endIcon={
                  fieldState.invalid && <IconNode src={Images.alertError} alt="error icon" />
                }
              />
            )}
          />
        </div>

        <div className={css.rowWrapper}>
          <label className={css.hazardousBoxWrapper}>
            {'' /* intentionally added */}
            <Controller
              name="haz"
              control={control}
              render={({ field }) => (
                <Checkbox {...field} checked={field.value} value={`${field.value}`} />
              )}
            />
            <IconNode src={Images.hazardGrey} alt="Hazard Icon" />
            <Typography variant="p">This product is hazardous.</Typography>
          </label>
        </div>
      </div>
    </BoxContainer>
  );
};

export default ProductDetailContainer;
